<template>
  <div
    :class="$style.activitiesListItemContainer"
    class="grid gapx-4"
  >
    <img
      :class="$style.activitiesListItemImage"
      :src="imageUrl"
      data-qa="image"
    >

    <span
      :class="$style.activitiesListItemName"
      :title="activityName"
      data-qa="name"
    >
      <img
        v-if="isSelection"
        :class="$style.activitiesListItemSelectionIcon"
        :title="$t('activities.selection_activity_tooltip')"
        src="/static/globe-solid.svg"
        data-qa="selection"
      >
      <span
        v-if="hasCode"
        data-qa="code"
      >
        ({{ activity.number }})
      </span>
      {{ activityName }}
    </span>

    <span
      v-if="hasDate"
      :class="$style.activitiesListItemDate"
      data-qa="date"
    >
      {{ parsedDate }}
    </span>

    <span
      v-if="hasPlace"
      :class="$style.activitiesListItemPlace"
      data-qa="place"
    >
      <img
        :class="$style.activitiesListItemPlaceIcon"
        src="/static/map-marker.svg"
      >
      {{ parsedPlace }}
    </span>

    <a
      v-if="activity.meta.see_more_url"
      :class="$style.activitiesListItemLink"
      :href="activity.meta.see_more_url"
      :target="linkTarget(activity.meta.see_more_url, activity.meta.avoid_target_top_in_hrefs)"
      data-qa="link"
    >
      {{ $t('activities.view_more') }}
    </a>
  </div>
</template>

<script>
import { ActivityModel } from '@/src/server/modules/activities/ActivityModel'
import { DateTime } from 'luxon'

export default {
  props: {
    activity: {
      required: true,
      type: ActivityModel,
    },
  },
  computed: {
    activityName () {
      if (this.activity.sourceType === 'competition') {
        return this.activity.meta.competition_data.name
      }

      return this.activity.name
    },
    hasPlace () {
      return (this.activity.sourceType === 'competition' && this.activity.meta.competition_data.venue_name) ||
        (this.activity.sourceType === 'manual' && this.activity.place) ||
        this.activity.country
    },
    hasCode () {
      return this.activity.number
    },
    parsedPlace () {
      let country

      if (this.activity.country) {
        country = this.activity.country.toUpperCase()
      }

      let place = this.activity.place

      if (this.activity.sourceType === 'competition') {
        place = this.activity.meta.competition_data.venue_name
      }

      return [place, country]
        .filter(magic => magic)
        .join(', ')
    },
    hasDate () {
      return (
        this.activity.sourceType === 'competition' &&
        (this.activity.meta.competition_data.start_date || this.activity.meta.competition_data.end_date)
      ) || (
        this.activity.sourceType === 'manual' &&
        (this.activity.startDate || this.activity.endDate)
      )
    },
    parsedDate () {
      let startDate = this.activity.startDate
      let endDate = this.activity.endDate

      if (this.activity.sourceType === 'competition') {
        startDate = this.activity.meta.competition_data.start_date
        endDate = this.activity.meta.competition_data.end_date
      }

      if (startDate && endDate) {
        return this.$t('activities.date_from_to', {
          from: startDate.toLocaleString(DateTime.DATE_SHORT),
          to: endDate.toLocaleString(DateTime.DATE_SHORT),
        })
      }

      if (startDate) {
        return this.$t('activities.date_from', {
          from: startDate.toLocaleString(DateTime.DATE_SHORT),
        })
      }

      if (endDate) {
        return this.$t('activities.date_to', {
          to: endDate.toLocaleString(DateTime.DATE_SHORT),
        })
      }
    },
    imageUrl () {
      if (this.activity.activityType && this.activity.activityType.name.toLocaleLowerCase().includes('master')) {
        return '/static/disciplines/master.png'
      }

      let disciplineId = null

      if (
        this.activity.disciplineIds &&
        Array.isArray(this.activity.disciplineIds) &&
        this.activity.disciplineIds.length === 1
      ) {
        disciplineId = this.activity.disciplineIds[0]
      }

      if (this.activity.sourceType === 'competition') {
        disciplineId = this.activity.meta.competition_data.discipline_id
      }

      if (disciplineId === '14') {
        return '/static/disciplines/water-polo.png'
      } else if (disciplineId === '38') {
        return '/static/disciplines/swimming.png'
      } else if (disciplineId === '97') {
        return '/static/disciplines/synchronised-swimming.png'
      } else if (disciplineId === '98') {
        return '/static/disciplines/diving.png'
      } else if (disciplineId === '110') {
        return '/static/disciplines/open-water-swimming.png'
      } else if (disciplineId === '155') {
        return '/static/disciplines/rowing.png'
      } else if (disciplineId === '158') {
        return '/static/disciplines/waterski.png'
      } else if (disciplineId === '186') {
        return '/static/disciplines/pararowing.png'
      } else if (disciplineId === '187') {
        return '/static/disciplines/traditional-rowing.png'
      } else if (disciplineId === '188') {
        return '/static/disciplines/sea-rowing.png'
      } else if (disciplineId === '189') {
        return '/static/disciplines/indoor-rowing.png'
      } else if (disciplineId === '159') {
        return '/static/disciplines/aeromodelling.png'
      } else if (disciplineId === '160') {
        return '/static/disciplines/ballooning.png'
      } else if (disciplineId === '161') {
        return '/static/disciplines/hang-gliding.png'
      } else if (disciplineId === '162') {
        return '/static/disciplines/paragliding.png'
      } else if (disciplineId === '163') {
        return '/static/disciplines/paramotor.png'
      } else if (disciplineId === '164') {
        return '/static/disciplines/skydiving.png'
      } else if (disciplineId === '165') {
        return '/static/disciplines/ultralight-aviation.png'
      } else if (disciplineId === '166') {
        return '/static/disciplines/gliding.png'
      } else if (disciplineId === '167') {
        return '/static/disciplines/aerobatics.png'
      } else if (disciplineId === '168') {
        return '/static/disciplines/powered-flight.png'
      } else if (disciplineId === '169') {
        return '/static/disciplines/cableski.png'
      } else if (disciplineId === '170') {
        return '/static/disciplines/cable-wakeboard.png'
      } else if (disciplineId === '171') {
        return '/static/disciplines/carreras.png'
      } else if (disciplineId === '172') {
        return '/static/disciplines/wakeboard.png'
      } else {
        return '/static/disciplines/default.png'
      }
    },
    isSelection () {
      if (this.activity.sourceType === 'competition') {
        return this.activity.meta.competition_data.is_selection
      }

      return this.activity.selection
    },
  },
}
</script>

<style lang="scss" module>
.activities-list-item {
  &__container {
    grid-template-areas:
      "image name"
      "image date"
      "image place"
      "image link";
    grid-template-columns: auto 1fr;

    @screen md {
      @apply items-center;
      grid-template-areas:
        "image name  link"
        "image date  link"
        "image place link";
      grid-template-columns: auto 1fr auto;
      grid-template-rows: 6fr 5fr 5fr;
    }
  }

  &__image {
    @apply w-16 h-16 rounded bg-brand mr-2;
    grid-area: image;

    @screen md {
      @apply w-24 h-24;
    }
  }

  &__name {
    @apply text-lg font-bold leading-tight mb-2;
    grid-area: name;

    @screen md {
      @apply text-xl truncate mb-0;
    }
  }

  &__date {
    @apply text-sm leading-none mb-2;
    grid-area: date;

    @screen md {
      @apply text-base truncate mb-0;
    }
  }

  &__place {
    @apply text-sm leading-none mb-2;
    grid-area: place;

    @screen md {
      @apply text-base truncate mb-0;
    }
  }

  &__place-icon {
    @apply inline-block w-3 mr-1 align-baseline -mb-px;
  }

  &__selection-icon {
    @apply inline-block w-5 mr-1 align-baseline -mb-px;
  }

  &__link {
    @apply inline-block px-4 py-1 rounded leading-loose no-underline bg-brand text-brand-text w-auto mt-4;
    grid-area: link;
    justify-self: start;

    &:hover {
      @apply bg-brand-light text-brand-text-light;
    }

    @screen md {
      @apply truncate mt-0;
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/server/components/Activities/ActivitiesListItem.vue